import { useMutation, useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute, Link, redirect } from "@tanstack/react-router"
import { GatedRegisterSessionPOSTSuccessResponse } from "ipctg-inflow-types/InFlowGatedAPI"
import { useEffect } from "react"
import { Controller, FormProvider, useForm, useWatch } from "react-hook-form"
import { Trans, useTranslation } from "react-i18next"

import api from "@/utilities/api"

import { Button } from "@/globalComponents/atoms/Button"
import Card from "@/globalComponents/atoms/Card"
import FieldError from "@/globalComponents/atoms/FieldError"
import Heading from "@/globalComponents/atoms/Heading"
import Icon from "@/globalComponents/atoms/Icon"
import Label from "@/globalComponents/atoms/Label"
import Step from "@/globalComponents/atoms/Step"
import Text from "@/globalComponents/atoms/Text"
import TextField from "@/globalComponents/molecules/TextField"
import {
  GatedLookupSessionsError,
  GatedRegisterSessionError,
} from "@/utilities/expectedErrors"
import { requiredRule } from "@/utilities/form"
import { meQueryOptions } from "@/utilities/hooks/queries/user"
import useToggle from "@/utilities/hooks/useToggle"
import {
  normalizePhoneNumber,
  removeUSCountryCode,
  stripNonPhoneCharacters,
} from "@/utilities/normalizers"

import { NoPhoneBasedSessionModal } from "../-components/NoPhoneBasedSessionModal"

export const Route = createFileRoute(
  "/qr/$qrLookupID/gated/register/contact-info"
)({
  validateSearch: (
    search: Record<string, unknown>
  ): { plate: string; sessionID?: string } => {
    return {
      plate: (search.plate as string) || "",
      sessionID: (search.sessionID as string) || "",
    }
  },
  beforeLoad: ({ search: { plate }, params: { qrLookupID } }) => {
    if (!plate) {
      throw redirect({
        to: "/qr/$qrLookupID/gated/register/look-up",
        params: { qrLookupID },
      })
    }
  },
  component: RouteComponent,
})

interface RegistrationFormSchema {
  plate: string
  phone: string
  sessionID: string
}

function RouteComponent() {
  const navigate = Route.useNavigate()
  const { plate, sessionID } = Route.useSearch()
  const { t } = useTranslation()
  const [showModal, toggleModal] = useToggle(false)
  const { data: me } = useSuspenseQuery(meQueryOptions())

  const requiresPhoneChallenge = !sessionID

  const form = useForm<RegistrationFormSchema>({
    defaultValues: {
      plate,
      phone:
        !requiresPhoneChallenge && me?.cognitoUser.phone_number
          ? normalizePhoneNumber(
              removeUSCountryCode(me.cognitoUser.phone_number)
            )
          : "",
      sessionID,
    },
    mode: "onBlur",
  })

  const phone = useWatch({
    control: form.control,
    name: "phone",
    defaultValue: "",
  })

  const { mutate, isPending, reset } = useMutation({
    mutationFn,
    onSuccess,
    throwOnError: true,
  })

  useEffect(() => {
    form.reset(undefined, { keepValues: true })
  }, [form.formState.isSubmitSuccessful])

  return (
    <>
      <Heading className="center" level={3} as={1}>
        {t("register.contactInfo.header")}
      </Heading>
      <Step step={2} of={4} />
      <Card>
        <Heading level={1} as={2}>
          {requiresPhoneChallenge
            ? t("register.contactInfo.subhead.challenge")
            : t("register.contactInfo.subhead.contact")}
        </Heading>
        {requiresPhoneChallenge && (
          <Text>
            <Trans i18nKey="register.contactInfo.description">
              If you used a phone number to enter the lot, please use the same
              number here. <strong>We need it to verify your session.</strong>
            </Trans>
          </Text>
        )}
        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <fieldset disabled={isPending}>
              <input type="hidden" {...form.register("plate")} />
              <input type="hidden" {...form.register("sessionID")} />
              <Controller<RegistrationFormSchema>
                name="phone"
                rules={{ required: requiredRule(t("errors.common.required")) }}
                render={({
                  field: { ref, ...rest },
                  fieldState: { error, invalid },
                }) => (
                  <TextField {...rest} isInvalid={invalid}>
                    <Label>{t("register.contactInfo.fieldLabel")}</Label>
                    {/* Thin space cause safari :/ */}
                    <TextField.Phone
                      innerRef={ref}
                      placeholder="​(123) 456-7890"
                    />
                    <FieldError>{error?.message}</FieldError>
                  </TextField>
                )}
              />
              <Button
                type="submit"
                hue="secondary"
                variant="default"
                isPending={isPending}
              >
                {t("common.continue")}
                <Icon name="arrowRight" slot="right" />
              </Button>
            </fieldset>
          </form>
        </FormProvider>
        {requiresPhoneChallenge && (
          <Text level={2}>
            {t("register.contactInfo.forgotChallenge")}{" "}
            <b>
              <Link from={Route.fullPath} to="/qr/$qrLookupID/help">
                {t("common.clickHere")}.
              </Link>
            </b>
          </Text>
        )}
      </Card>
      <Text
        level={3}
        style={{ color: "var(--neutral-11)", textAlign: "center" }}
      >
        {t("common.piUsage")}
      </Text>
      <NoPhoneBasedSessionModal
        isOpen={showModal}
        onOpenChange={toggleModal}
        phone={phone}
        resetMutation={reset}
        onExit={() =>
          navigate({
            to: "/qr/$qrLookupID/welcome",
            replace: true,
          })
        }
      />
    </>
  )

  function onSubmit(data: RegistrationFormSchema) {
    mutate(data)
  }

  async function mutationFn(data: RegistrationFormSchema) {
    const phone = stripNonPhoneCharacters(data.phone)
    let sessionID = data.sessionID
    if (!data.sessionID) {
      const lookupRsp = await api.lookupSessions.POST({
        ...data,
        phone,
        method: "phone",
      })

      if ("failReason" in lookupRsp) {
        throw new GatedLookupSessionsError(lookupRsp.failReason)
      }
      const session = lookupRsp.sessions.find((s) => !s.isRegistered)
      if (!session) {
        toggleModal()
        return
      } else {
        sessionID = session.sessionID
      }
    }

    const registerRsp = await api.registerSession.POST({
      sessionID,
      phone,
      isInLane: false,
      isReregistering: false,
    })

    if ("failReason" in registerRsp) {
      throw new GatedRegisterSessionError(registerRsp.failReason)
    }

    return registerRsp
  }

  function onSuccess(rsp: GatedRegisterSessionPOSTSuccessResponse | undefined) {
    if (rsp) {
      navigate({
        to: "/qr/$qrLookupID/gated/register/$sessionID/cart",
        params: { sessionID: rsp.session.sessionID },
      })
    }
  }
}
